import { useLayoutEffect, useRef } from 'react';

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789{}<>/#+=*_';
const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// The site's button hover: the label scrambles from left to right and settles back.
// The text is written by this effect, not by React, so the animation never fights reconciliation.
export const Scramble = ({ children }: { children: string }) => {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const span = ref.current;
    if (!span) return;
    span.textContent = children;
    const host = span.closest('a, button');
    if (!host || reduce) return;
    let raf = 0;
    let busy = false;
    const run = () => {
      if (busy || (host as HTMLButtonElement).disabled) return;
      busy = true;
      const core = children;
      span.style.width = `${span.getBoundingClientRect().width}px`;
      const t0 = performance.now();
      const dur = 240 + core.length * 12;
      const tick = (now: number) => {
        const k = (now - t0) / dur;
        span.textContent = '';
        for (let i = 0; i < core.length; i++) {
          const ch = core.charAt(i);
          const at = (i / core.length) * 0.66;
          if (ch === ' ' || k < at || k > at + 0.34) {
            span.appendChild(document.createTextNode(ch));
          } else {
            const g = document.createElement('i');
            g.className = 'sg';
            g.textContent = GLYPHS.charAt(Math.floor(Math.random() * GLYPHS.length));
            span.appendChild(g);
          }
        }
        if (k < 1) raf = requestAnimationFrame(tick);
        else {
          span.textContent = core;
          span.style.width = '';
          busy = false;
        }
      };
      raf = requestAnimationFrame(tick);
    };
    const onFocus = () => {
      if ((host as HTMLElement).matches(':focus-visible')) run();
    };
    host.addEventListener('mouseenter', run);
    host.addEventListener('focus', onFocus);
    return () => {
      cancelAnimationFrame(raf);
      host.removeEventListener('mouseenter', run);
      host.removeEventListener('focus', onFocus);
      span.textContent = children;
      span.style.width = '';
    };
  }, [children]);

  return <span className="sc" ref={ref} />;
};
